/**
 * Human-readable citation titles for AiWarden (IRL vs VDR).
 */

/** Bracketed ref for display, e.g. [1] */
export function formatCitationRefBracket(value) {
    const n = normalizeCitationRefValue(value);
    return n ? `[${n}]` : '';
}

export function linkCitationChunk(citation, chunks) {
    if (!citation) return null;
    const list = Array.isArray(chunks) ? chunks : [];
    if (Number.isInteger(citation.merged_chunk_index) && list[citation.merged_chunk_index]) {
        return list[citation.merged_chunk_index];
    }
    if (citation.chunk_id) {
        const byChunkId = list.find(x => x.chunk_id === citation.chunk_id);
        if (byChunkId) return byChunkId;
    }
    const refNum = parseInt(String(citation.ref || '').replace(/[^\d]/g, ''), 10);
    if (!Number.isNaN(refNum) && list[refNum - 1]) {
        return list[refNum - 1];
    }
    const preview = (citation.text_preview || citation.chunk || '').trim();
    if (preview) {
        return list.find(ch => (ch.chunk || ch.text_preview || '').trim() === preview) || null;
    }
    return null;
}

export function normalizeCitationRefValue(value) {
    const raw = String(value || '').trim();
    const bracketed = raw.match(/^\[(\d+)\]$/);
    if (bracketed) return bracketed[1];
    const numeric = raw.match(/^(\d+)$/);
    return numeric ? numeric[1] : '';
}

export function getAiWardenCitationTargetId(scope, citationOrRef) {
    const ref = normalizeCitationRefValue(citationOrRef?.ref ?? citationOrRef);
    return ref ? `ai-warden-${scope}-citation-${ref}` : undefined;
}

function normalizeSourceType(citation, linkedChunk) {
    const raw = citation?.source_type || linkedChunk?.source_type || '';
    return String(raw).toLowerCase().trim();
}

export function isObservationCitation(citation, linkedChunk) {
    const st = normalizeSourceType(citation, linkedChunk);
    if (st === 'observation') return true;
    if (citation?.observation_id != null || linkedChunk?.observation_id != null) return true;
    if (citation?.observation_ref || linkedChunk?.observation_ref) return true;
    return false;
}

export function isIrlCitation(citation, linkedChunk) {
    if (isObservationCitation(citation, linkedChunk)) return false;
    const st = normalizeSourceType(citation, linkedChunk);
    if (st === 'vdr_chunk' || st === 'vdr') return false;
    if (st === 'irl' || st === 'irl_row') return true;
    const hasFile = citation?.file_id != null || linkedChunk?.file_id != null;
    if (hasFile) return false;
    return Boolean(
        citation?.irl_ref ||
            citation?.irl_id != null ||
            citation?.request_excerpt ||
            citation?.answer_excerpt ||
            linkedChunk?.request_excerpt,
    );
}

/** CSS module class for inline/card citation ref by source (vdr vs irl vs observation). */
export function getCitationRefClassName(styles, citation, linkedChunk) {
    if (!styles) return '';
    if (isObservationCitation(citation, linkedChunk)) return styles.aiWardenCitationRefObservation;
    return isIrlCitation(citation, linkedChunk) ? styles.aiWardenCitationRefIrl : styles.aiWardenCitationRefVdr;
}

/** CSS module class for markdown inline citation links. */
export function getInlineCitationLinkClassName(styles, citation, linkedChunk) {
    if (!styles) return '';
    if (isObservationCitation(citation, linkedChunk)) return styles.aiWardenInlineCitationLinkObservation;
    return isIrlCitation(citation, linkedChunk) ? styles.aiWardenInlineCitationLinkIrl : styles.aiWardenInlineCitationLinkVdr;
}

function resolveObservationWorkstreamTag(citation, linkedChunk) {
    const ws =
        pickField(citation, linkedChunk, 'workstream_repr', 'workstream_name') ||
        'Workstream';
    const tag = pickField(citation, linkedChunk, 'tag_repr', 'tag') || 'Tag';
    return { ws: String(ws).trim(), tag: String(tag).trim() };
}

function pickField(citation, linkedChunk, ...keys) {
    for (const key of keys) {
        const fromCite = citation?.[key];
        if (fromCite != null && fromCite !== '') return fromCite;
        const fromChunk = linkedChunk?.[key];
        if (fromChunk != null && fromChunk !== '') return fromChunk;
    }
    return null;
}

/** Read-only IRL detail payload for AiWarden sidebar (from citation + linked chunk). */
export function buildIrlCitationDetail(citation, linkedChunk = null, ctx = {}) {
    const { workstreams = [], folderRows = [], activeWsName = null } = ctx;
    const workstreamId = pickField(citation, linkedChunk, 'workstream_id');
    const workstreamName =
        pickField(citation, linkedChunk, 'workstream_name') ||
        resolveWorkstreamNameFromId(workstreamId, workstreams, activeWsName);

    const tag = resolveFolderLabel(citation, linkedChunk, folderRows);
    const responseStatus = pickField(citation, linkedChunk, 'response_status', 'status');
    const hasResponse = citation?.has_response ?? linkedChunk?.has_response;

    return {
        ref: citation?.ref ?? null,
        irl_ref: pickField(citation, linkedChunk, 'irl_ref'),
        irl_id: pickField(citation, linkedChunk, 'irl_id'),
        workstream_name: workstreamName,
        workstream_id: workstreamId,
        tag,
        question: pickField(citation, linkedChunk, 'request_excerpt', 'question'),
        answer: pickField(citation, linkedChunk, 'answer_excerpt', 'answer', 'management_response'),
        status: responseStatus,
        has_response: hasResponse,
        intent_class: pickField(citation, linkedChunk, 'intent_class', 'chunk_intent'),
        evidence_score: citation?.evidence_score ?? linkedChunk?.['@search.score'] ?? null,
        excerpt: (linkedChunk?.chunk || citation?.text_preview || '').trim(),
    };
}

export function hasOpenableIrlCitation(citation, linkedChunk) {
    if (!isIrlCitation(citation, linkedChunk)) return false;
    const detail = buildIrlCitationDetail(citation, linkedChunk);
    return Boolean(
        detail.irl_ref ||
            detail.irl_id != null ||
            detail.question ||
            detail.answer ||
            detail.excerpt ||
            detail.tag,
    );
}

function resolveWorkstreamNameFromId(workstreamId, workstreams, fallbackName) {
    if (workstreamId != null) {
        const ws = (workstreams || []).find(w => String(w.id) === String(workstreamId));
        if (ws?.name) return ws.name;
    }
    return fallbackName || null;
}

function resolveFolderLabel(citation, linkedChunk, folderRows) {
    const tag = (citation?.tag || linkedChunk?.tag || citation?.intent_class || linkedChunk?.chunk_intent || '').trim();
    if (tag) return tag;
    const folderId = citation?.folder_id ?? linkedChunk?.folder_id;
    if (folderId != null && folderRows?.length) {
        const row = folderRows.find(r => String(r.id) === String(folderId));
        if (row?.name) return row.name;
    }
    return null;
}

function resolveVdrFileName(citation, linkedChunk, modalFiles) {
    const fromPayload =
        citation?.document_name ||
        citation?.file_name ||
        citation?.metadata_storage_name ||
        linkedChunk?.document_name ||
        linkedChunk?.metadata_storage_name ||
        linkedChunk?.file_name;

    if (fromPayload && String(fromPayload).trim() && !/^document$/i.test(fromPayload)) {
        return String(fromPayload).trim();
    }

    const fileId = citation?.file_id ?? linkedChunk?.file_id;
    if (fileId != null && modalFiles?.length) {
        const doc = modalFiles.find(f => String(f.id) === String(fileId));
        const name = doc?.document_name || doc?.name;
        if (name && String(name).trim()) return String(name).trim();
    }

    if (fileId != null) return `File ${fileId}`;
    return null;
}

/**
 * @param {object} citation
 * @param {object|null} linkedChunk
 * @param {{ workstreams?: array, folderRows?: array, modalFiles?: array, activeWsName?: string|null }} ctx
 */
export function getCitationDisplayTitle(citation, linkedChunk = null, ctx = {}) {
    const { workstreams = [], folderRows = [], modalFiles = [], activeWsName = null } = ctx;

    if (isObservationCitation(citation, linkedChunk)) {
        const { ws, tag } = resolveObservationWorkstreamTag(citation, linkedChunk);
        return `Observation: ${ws} / ${tag}`;
    }

    if (isIrlCitation(citation, linkedChunk)) {
        const wsName =
            citation?.workstream_name ||
            linkedChunk?.workstream_name ||
            resolveWorkstreamNameFromId(
                citation?.workstream_id ?? linkedChunk?.workstream_id,
                workstreams,
                activeWsName,
            ) ||
            'Workstream';

        const folder =
            resolveFolderLabel(citation, linkedChunk, folderRows) ||
            (citation?.irl_ref ? String(citation.irl_ref).split('.')[1] : null) ||
            'Folder';

        const refSuffix = citation?.irl_ref ? ` · ${citation.irl_ref}` : '';
        return `IRL — ${wsName} / ${folder}${refSuffix}`;
    }

    const fileName = resolveVdrFileName(citation, linkedChunk, modalFiles);
    if (fileName) return fileName;

    return citation?.title || 'Document';
}

const SWOT_LABELS = { S: 'Strength', W: 'Weakness', O: 'Opportunity', T: 'Threat' };

/** Read-only observation detail for AiWarden sidebar. */
export function buildObservationCitationDetail(citation, linkedChunk = null) {
    const { ws, tag } = resolveObservationWorkstreamTag(citation, linkedChunk);
    const observationText = pickField(
        citation,
        linkedChunk,
        'observation_text',
        'observation',
    );
    const comments = pickField(citation, linkedChunk, 'comments_text', 'comments');
    const excerpt = (linkedChunk?.chunk || citation?.text_preview || '').trim();
    const swotCode = pickField(citation, linkedChunk, 'swot');

    return {
        ref: citation?.ref ?? null,
        observation_id: pickField(citation, linkedChunk, 'observation_id'),
        observation_ref: pickField(citation, linkedChunk, 'observation_ref'),
        workstream_repr: ws,
        tag_repr: tag,
        assessment: pickField(citation, linkedChunk, 'assessment'),
        swot: swotCode,
        swot_label: swotCode ? SWOT_LABELS[String(swotCode).toUpperCase()] || swotCode : null,
        impact: pickField(citation, linkedChunk, 'impact'),
        observation_text: observationText,
        comments,
        excerpt,
        evidence_score: citation?.evidence_score ?? linkedChunk?.['@search.score'] ?? null,
    };
}

export function hasOpenableObservationCitation(citation, linkedChunk) {
    if (!isObservationCitation(citation, linkedChunk)) return false;
    const detail = buildObservationCitationDetail(citation, linkedChunk);
    return Boolean(
        detail.observation_id != null ||
            detail.observation_ref ||
            detail.observation_text ||
            detail.comments ||
            detail.excerpt ||
            detail.tag_repr,
    );
}

/** Merge evidence metadata onto citations for display (intelligence ask). */
export function enrichCitationsFromEvidence(citations, evidence) {
    const evList = Array.isArray(evidence) ? evidence : [];
    return (Array.isArray(citations) ? citations : []).map(c => {
        const m = String(c.ref || '').match(/\[?(\d+)\]?/);
        const idx = m ? Number(m[1]) - 1 : -1;
        const ev = idx >= 0 ? evList[idx] : null;
        const meta = ev?.metadata && typeof ev.metadata === 'object' ? ev.metadata : {};
        const raw = ev?.raw && typeof ev.raw === 'object' ? ev.raw : {};

        return {
            ...c,
            source_type: c.source_type || ev?.source_type || meta.source_type,
            irl_ref: c.irl_ref || meta.irl_ref || raw.irl_ref,
            irl_id: c.irl_id ?? meta.irl_id ?? raw.irl_id,
            tag: c.tag || meta.tag || raw.tag,
            workstream_name: c.workstream_name || meta.workstream_name || raw.workstream_name,
            workstream_id: c.workstream_id ?? meta.workstream_id ?? raw.workstream_id,
            request_excerpt: c.request_excerpt || meta.request_excerpt || raw.request_text || raw.request_excerpt,
            answer_excerpt: c.answer_excerpt || meta.answer_excerpt || raw.answer_text || raw.answer_excerpt,
            response_status: c.response_status || meta.response_status || raw.response_status || meta.status || raw.status,
            has_response: c.has_response ?? meta.has_response ?? raw.has_response,
            intent_class: c.intent_class || meta.intent_class || raw.intent_class,
            document_name:
                c.document_name ||
                meta.metadata_storage_name ||
                meta.document_name ||
                raw.metadata_storage_name ||
                raw.document_name,
            file_id: c.file_id ?? meta.file_id ?? raw.file_id ?? meta.referenced_file_id,
            chunk_id: c.chunk_id || meta.chunk_id || raw.chunk_id,
            chunk_topics: c.chunk_topics || meta.chunk_topics || raw.chunk_topics,
            chunk_intent: c.chunk_intent || meta.chunk_intent || raw.chunk_intent,
            chunk_doc_type: c.chunk_doc_type || meta.chunk_doc_type || raw.chunk_doc_type,
            text_preview: c.text_preview || ev?.text_preview || meta.text_preview,
            keywords: c.keywords || meta.keywords || raw.keywords,
            observation_id: c.observation_id ?? meta.observation_id ?? raw.observation_id,
            observation_ref: c.observation_ref || meta.observation_ref || raw.observation_ref,
            workstream_repr:
                c.workstream_repr || meta.workstream_repr || raw.workstream_repr,
            tag_repr: c.tag_repr || meta.tag_repr || raw.tag_repr,
            assessment: c.assessment || meta.assessment || raw.assessment,
            swot: c.swot || meta.swot || raw.swot,
            impact: c.impact || meta.impact || raw.impact,
            observation_text:
                c.observation_text ||
                meta.observation_text ||
                raw.observation_text ||
                ev?.text_preview,
            comments_text: c.comments_text || meta.comments_text || raw.comments_text,
            comments: c.comments || meta.comments || raw.comments,
        };
    });
}
